import { expoClient } from "@-/com.npmjs.better-auth__expo/client";
import { dashClient, sentinelClient } from "@-/com.npmjs.better-auth__infra/client";
import { sentinelNativeClient } from "@-/com.npmjs.better-auth__infra/native";
import { type BetterFetchPlugin, createAuthClient } from "@-/com.npmjs.better-auth/react";
import { inferAdditionalFields, twoFactorClient } from "@-/com.npmjs.better-auth/client/plugins";
import { AUTH_USER_FIELDS } from "@-/dev.improve.haniverse.configs.auth/auth-user-fields";
import Constants from "@-/com.npmjs.expo-constants";
import { Platform } from "@-/com.npmjs.react-native";
import * as SecureStore from "@-/com.npmjs.expo-secure-store";
import onetime from "@-/com.npmjs.onetime";
import {
  fetchWithDeadline,
  type UnboundedWait,
  unboundedWait,
} from "@-/dev.improve.haniverse.lib.deadline";
import { ENV } from "@-/com.npmjs.varlock/env";
import { isLocalDevServerHostname } from "@-/dev.improve.haniverse.lib.dev-server-host";
import { resolveBetterAuthKvIdentifyUrl } from "#better-auth-infra-options";
import { clearAuthSessionOnFreshInstall } from "#clear-auth-session-on-fresh-install";

// expo-secure-store has no web implementation. On native we use it directly so
// the better-auth session cookie ends up in the iOS/Android keychain; on web we
// fall back to `localStorage`, which already gives us per-origin persistence.
// `@better-auth/expo` 1.7 reads and writes through the async half of the storage
// contract, so the sync `localStorage` calls are exposed under both names.
const webStorage = {
  getItem(key: string): string | null {
    if (globalThis.localStorage === undefined) {
      return null;
    }
    return globalThis.localStorage.getItem(key);
  },
  async getItemAsync(key: string): Promise<string | null> {
    return webStorage.getItem(key);
  },
  setItem(key: string, value: string): void {
    if (globalThis.localStorage === undefined) {
      return;
    }
    globalThis.localStorage.setItem(key, value);
  },
  async setItemAsync(key: string, value: string): Promise<void> {
    webStorage.setItem(key, value);
  },
};

const TRAILING_SLASH_REGEX = /\/$/;
const LOOPBACK_HOST_REGEX = /^(?:127(?:\.\d+){3}|\[?::1\]?|localhost)$/i;
const LOCAL_STANDALONE_BACKEND_URL = "http://localhost:4574";

function getAuthStorage() {
  return Platform.OS === "web" ? webStorage : SecureStore;
}

const SENTINEL_VISITOR_STORAGE_PREFIX = "haniverse-sentinel";

export function getSentinelVisitorStorageKey(key: string): string {
  return `${SENTINEL_VISITOR_STORAGE_PREFIX}.${key}`;
}

const sentinelVisitorStorage = {
  async getItem(key: string): Promise<string | null> {
    const storageKey = getSentinelVisitorStorageKey(key);
    if (Platform.OS === "web") {
      return webStorage.getItem(storageKey);
    }
    return SecureStore.getItemAsync(storageKey);
  },
  async setItem(key: string, value: string): Promise<void> {
    const storageKey = getSentinelVisitorStorageKey(key);
    if (Platform.OS === "web") {
      webStorage.setItem(storageKey, value);
      return;
    }
    await SecureStore.setItemAsync(storageKey, value);
  },
};

// Fallback OAuth-callback scheme, used ONLY when `Constants.expoConfig?.scheme` is somehow absent
// (the config always sets it, so this is a defensive last resort). Key it off the resolved app
// variant (app-config writes `extra.appVariant = EXPO_PUBLIC_APP_VARIANT`) so a `local` build falls
// back to its own `haniverse-local` rather than `haniverse-dev`; other/unknown values keep the old
// NODE_ENV-based prod/dev split. Must stay in sync with VARIANT_CONFIG[variant].scheme in app-config.
function getDefaultBetterAuthScheme(): string {
  const appVariant = Constants.expoConfig?.extra?.appVariant;
  if (appVariant === "local") {
    return "haniverse-local";
  }
  if (appVariant === "staging") {
    return "haniverse-staging";
  }
  if (appVariant === "alpha") {
    return "haniverse-alpha";
  }
  if (appVariant === "production") {
    return "haniverse";
  }
  return process.env.NODE_ENV === "production" ? "haniverse" : "haniverse-dev";
}

function isLoopbackUrl(url: string): boolean {
  try {
    return LOOPBACK_HOST_REGEX.test(new URL(url).hostname);
  } catch {
    return false;
  }
}

function normalizeBetterAuthBaseUrl(url: string): string {
  return url.replace(TRAILING_SLASH_REGEX, "");
}

function readExpoPublicBetterAuthUrl(): string {
  const url = ENV.EXPO_PUBLIC_BETTER_AUTH_URL?.trim();
  if (url == null || url.length === 0) {
    throw new Error("EXPO_PUBLIC_BETTER_AUTH_URL is required.");
  }
  return normalizeBetterAuthBaseUrl(url);
}

function isExplicitLocalBackendBuild(): boolean {
  return (
    ENV.EXPO_PUBLIC_HANIVERSE_LOCAL_BACKEND === true ||
    Constants.expoConfig?.extra?.localBackendBuild === true
  );
}

/**
 * Base URL of the Better Auth API. The API is served by the Expo dev server's
 * `output: "server"` mode under `/api/auth/*`, so we reuse Metro's hostUri.
 * Set `EXPO_PUBLIC_BETTER_AUTH_URL` to override (e.g. `http://127.0.0.1:4574`).
 */
export function getBetterAuthBaseUrl(): string {
  const envUrl = readExpoPublicBetterAuthUrl();
  const onWeb = Platform.OS === "web" && globalThis.location !== undefined;
  if (onWeb) {
    const pageOrigin = globalThis.location.origin;
    const pageIsLoopback = LOOPBACK_HOST_REGEX.test(globalThis.location.hostname);
    // The better-auth + oRPC API is co-deployed under `/api/*` on EVERY domain that
    // serves this web bundle (haniverse.app, staging.haniverse.app, haniverse.expo.app, preview
    // *.workers.dev, localhost). The inlined
    // EXPO_PUBLIC_BETTER_AUTH_URL only names ONE of those, so a bundle served from
    // haniverse.app would otherwise fire cross-origin RPCs at haniverse.expo.app —
    // tripping CORS (the handler reflects its OWN origin, not the request's) and, for
    // loopback values, Firefox's Local Network Access prompt. When the page is on a
    // real (non-loopback) host, always prefer the page origin so fetches stay
    // same-origin. Only honor the inlined URL when the page itself is loopback and
    // the env value points at the SAME loopback origin (dev pointing at its own server).
    if (!pageIsLoopback) {
      return pageOrigin;
    }
    try {
      if (isLoopbackUrl(envUrl) && new URL(envUrl).origin === pageOrigin) {
        return envUrl;
      }
    } catch {
      return pageOrigin;
    }
    return pageOrigin;
  }
  // Native. Derive the API origin from the host the dev-client actually loaded its
  // bundle from (`hostUri`), and prefer it over a loopback `EXPO_PUBLIC_BETTER_AUTH_URL`
  // when that host is REMOTE. Why: a dev EAS build inlines the dev-default
  // `EXPO_PUBLIC_BETTER_AUTH_URL=http://127.0.0.1:4574`, but when the app is launched
  // against a TUNNEL (CI maestro deep-links `?url=https://metro.haniverse.dev`)
  // `127.0.0.1` is the DEVICE's own loopback — harmless on the iOS sim (shares the host
  // loopback) but on the Android emulator it's the emulator itself, so Better Auth fetches
  // `127.0.0.1:4574` and dies with ConnectException → sign-in/seed flows fail. The app
  // demonstrably reached its server at `hostUri` (the bundle came from there), so that's
  // the authoritative API origin. A loopback env override only wins when hostUri is ALSO
  // loopback/absent (a genuine local-dev pointing at its own server).
  const hostBaseUrl = betterAuthBaseUrlFromHostUri(Constants.expoConfig?.hostUri);
  // A present hostUri means this is a dev-client bundle, and its server co-hosts the API. Treat
  // that runtime value as authoritative even when both it and the inlined env URL are loopback.
  // Metro's transform cache is shared between isolated `--port` stacks and does not key cached
  // modules by EXPO_PUBLIC_* values, so a stack on 5707 can otherwise inherit a cached 5701 auth
  // URL. The bundle demonstrably reached hostUri; a stale inlined loopback URL may be unreachable.
  if (hostBaseUrl != null) {
    return hostBaseUrl;
  }
  // Standalone release builds (alpha/staging/production on TestFlight / Play) have no dev-server
  // `hostUri`. A loopback `EXPO_PUBLIC_BETTER_AUTH_URL` baked from CI's dev Infisical env
  // is the device's own loopback — sign-in and every `/api/*` call fail. Dev-client builds
  // still reach the host via `hostUri` and keep honoring the loopback env override.
  const hostUri = Constants.expoConfig?.hostUri;
  const isStandaloneBundle = hostUri == null || hostUri.length === 0;
  const appVariant = Constants.expoConfig?.extra?.appVariant;
  const isStandaloneReleaseBuild =
    appVariant === "alpha" || appVariant === "staging" || appVariant === "production";
  if (
    isStandaloneBundle &&
    isStandaloneReleaseBuild &&
    envUrl !== LOCAL_STANDALONE_BACKEND_URL &&
    Platform.OS !== "web" &&
    isLoopbackUrl(envUrl) &&
    !isExplicitLocalBackendBuild()
  ) {
    throw new Error(
      `Refusing to use loopback EXPO_PUBLIC_BETTER_AUTH_URL=${envUrl} in a standalone build. ` +
        "Set EXPO_PUBLIC_BETTER_AUTH_URL to the deployed Better Auth origin, or set " +
        "EXPO_PUBLIC_HANIVERSE_LOCAL_BACKEND=1 for an intentional local-backend build.",
    );
  }
  return envUrl;
}

/**
 * Turn Expo's `hostUri` (the host the dev-client loaded the bundle from) into the
 * Better Auth API origin. The API is co-served with the bundle under `/api/auth/*`.
 *   - loopback, IP-literal or `.local` host (e.g. `127.0.0.1:4574`, `192.168.1.5:5701`,
 *     `100.101.100.116:4574`; see `isLocalDevServerHostname`): the local
 *     Expo dev server → preserve its port. Legacy `:8081` manifests still map to the default
 *     `__expo-dev.ts` API port (4574), where `/api` is served separately.
 *   - any other host (a real domain — the Cloudflare tunnel, e.g. `metro.haniverse.dev`
 *     possibly suffixed `:443`/`:8081`): TLS-terminated and serves `/api` on 443 →
 *     `https://<host>` (drop the embedded Metro port).
 */
function betterAuthBaseUrlFromHostUri(hostUri: string | undefined): string | undefined {
  if (hostUri == null || hostUri.length === 0) {
    return undefined;
  }
  let parsed: URL;
  try {
    parsed = new URL(`http://${hostUri}`);
  } catch {
    return undefined;
  }
  if (isLocalDevServerHostname(parsed.hostname)) {
    const port = parsed.port === "" || parsed.port === "8081" ? "4574" : parsed.port;
    return `http://${parsed.hostname}:${port}`;
  }
  return `https://${parsed.hostname}`;
}

export function getBetterAuthScheme(): string {
  const scheme = Constants.expoConfig?.scheme;
  if (typeof scheme === "string" && scheme.length > 0) {
    return scheme;
  }
  if (Array.isArray(scheme)) {
    return scheme.find((candidate) => candidate.length > 0) ?? getDefaultBetterAuthScheme();
  }
  return getDefaultBetterAuthScheme();
}

function buildBetterAuthInfraClientPlugins() {
  const identifyUrl = resolveBetterAuthKvIdentifyUrl();
  const sentinelOptions = {
    autoSolveChallenge: true as const,
    identifyUrl,
  };

  return [
    dashClient(),
    Platform.OS === "web"
      ? sentinelClient(sentinelOptions)
      : sentinelNativeClient({
          ...sentinelOptions,
          storage: sentinelVisitorStorage,
        }),
  ];
}

// better-auth/expo stores the session under `<storagePrefix>_cookie` and `<storagePrefix>_session_data`.
const AUTH_STORAGE_PREFIX = "haniverse";

/**
 * One Better Auth round trip, headers AND body.
 *
 * better-fetch has no default timeout and React Native's `fetch` has none either, so a backend that
 * accepted the connection and never answered left the sign-in / sign-up / 2FA buttons spinning,
 * `signOut` pending, and on web `useAuthSession` unresolved — forever. Every auth endpoint is a
 * single server round trip (the slowest, sign-up, hashes a password and sends one email), so 20s is
 * generous on cellular while still ending a hung request with a retryable "timed out" error.
 */
const AUTH_REQUEST_DEADLINE_MS = 20_000;

/**
 * better-fetch's own abort timer, set slightly ABOVE {@link AUTH_REQUEST_DEADLINE_MS} so it never
 * races the deadline on requests that go through `customFetchImpl`. It exists for the one request
 * that does not: Better Auth Infra's proof-of-work retry calls the global `fetch` directly and only
 * bounds it when the request carries a `timeout`.
 */
const AUTH_FETCH_TIMEOUT_BACKSTOP_MS = AUTH_REQUEST_DEADLINE_MS + 5000;

/**
 * The `meta.deadline` of a mutation that waits on a person in Apple's or Google's sign-in sheet.
 *
 * The person may take as long as they like there (typing a password, finding a 2FA code), so a
 * timer would only fail a sign-in that is still in progress. The wait ends on the provider's own
 * completion event instead: Sign in with Apple (`ASAuthorizationController`) and the browser auth
 * session (`ASWebAuthenticationSession` on iOS; Custom Tabs plus the return to the foreground on
 * Android) always report success or cancellation, and cancelling releases the button. Every server
 * round trip around the sheet stays bounded ({@link AUTH_REQUEST_DEADLINE_MS} per request).
 */
export function interactiveSignInDeadline(): UnboundedWait {
  return unboundedWait(
    "interactive sign-in: the Apple/Google sheet always reports success or cancellation, however long the person takes",
  );
}

function readRequestUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") {
    return input;
  }
  return input instanceof URL ? input.href : input.url;
}

async function fetchBetterAuthWithDeadline(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const url = readRequestUrl(input);
  const response = await fetchWithDeadline(input, {
    ...init,
    deadline: AUTH_REQUEST_DEADLINE_MS,
    operation: `Better Auth ${url.split("?")[0]}`,
  });
  // The buffered `Response` is constructed locally and so has an empty `url`. Better Auth Infra's
  // proof-of-work solver re-posts to `response.url`, so restore it.
  if (response.url === "") {
    Object.defineProperty(response, "url", { value: url });
  }
  return response;
}

function userFieldsClient() {
  return inferAdditionalFields({ user: AUTH_USER_FIELDS });
}

/**
 * Publishes a sign-out to `useSession()` as soon as `/sign-out` succeeds, on web.
 *
 * Native already does: `@better-auth/expo` clears the session atom on `/sign-out`
 * (`clearSessionCache`), but returns early on web. There the atom kept the signed-out user until the
 * `get-session` refetch the sign-out signal starts had answered, since Better Auth keeps the previous
 * `data` while it refetches. The sign-out navigates to `/auth` before that, and web's Log In card read
 * the stale user as a fresh sign-in: it provisioned the signed-out account (four "Not signed in"
 * `database.actor.ensure` failures) and sent the browser back to Learn's map.
 */
function webSignOutSessionClient() {
  let store: Pick<HanAuthClient["$store"], "atoms"> | undefined;
  const clearSessionOnSignOut: BetterFetchPlugin = {
    hooks: {
      onSuccess(context) {
        if (!new URL(context.request.url).pathname.endsWith("/sign-out")) {
          return;
        }
        const session = store?.atoms.session;
        session?.set({ ...session.get(), data: null, error: null, isPending: false });
      },
    },
    id: "han-web-sign-out-session",
    name: "Han web sign-out session",
  };
  return {
    fetchPlugins: Platform.OS === "web" ? [clearSessionOnSignOut] : [],
    getActions(_fetch: unknown, $store: Pick<HanAuthClient["$store"], "atoms">) {
      store = $store;
      return {};
    },
    id: "han-web-sign-out-session",
  } as const;
}

function createHanAuthClient() {
  clearAuthSessionOnFreshInstall([
    `${AUTH_STORAGE_PREFIX}_cookie`,
    `${AUTH_STORAGE_PREFIX}_session_data`,
  ]);
  return createAuthClient({
    // Prefer the runtime resolver: on web it reconciles the base URL against the
    // page origin so a `localhost` page doesn't fetch `127.0.0.1` (different CORS
    // origin → get-session preflight fails → session provider never mounts).
    // Falls back to the static env value when the resolver can't determine one.
    baseURL: getBetterAuthBaseUrl(),
    fetchOptions: {
      customFetchImpl: fetchBetterAuthWithDeadline,
      timeout: AUTH_FETCH_TIMEOUT_BACKSTOP_MS,
    },
    plugins: [
      expoClient({
        scheme: Constants.expoConfig?.scheme?.toString(),
        storage: getAuthStorage(),
        storagePrefix: AUTH_STORAGE_PREFIX,
      }),
      webSignOutSessionClient(),
      // Mirrors the server `twoFactor()` plugin so `getAuthClient().twoFactor.*`
      // (enable / verifyTotp / verifyBackupCode / disable / generateBackupCodes /
      // getTotpUri) and the `data.twoFactorRedirect` sign-in signal are available.
      twoFactorClient(),
      userFieldsClient(),
      ...buildBetterAuthInfraClientPlugins(),
    ],
  });
}

type HanAuthClient = ReturnType<
  typeof createAuthClient<{
    plugins: [
      ReturnType<typeof expoClient>,
      ReturnType<typeof twoFactorClient>,
      ReturnType<typeof userFieldsClient>,
    ];
  }>
>;

// Conditional infra plugins widen the inferred client type beyond the expo-only
// baseline we use for session typing.
// oxlint-disable-next-line typescript/no-unsafe-type-assertion -- runtime plugin set is a superset of the typed baseline
export const getAuthClient = onetime(createHanAuthClient) as unknown as () => HanAuthClient;

/**
 * The stored session cookie, for an authenticated request Better Auth does not make itself (oRPC).
 *
 * Native keeps the session in SecureStore, so every such request must attach it explicitly. Read
 * through the TYPED action and awaited: `@better-auth/expo` 1.7 turned `getCookie()` from a sync
 * string into a `Promise<string>`, and the oRPC link's former untyped `Reflect.get` +
 * `typeof === "string"` read kept compiling while it silently dropped the cookie from EVERY
 * request. Each `database.actor.ensure` then failed "Not signed in", so every sign-in since the
 * bump left the device local-only (staging, 2026-10-02). A web page's own cookies already travel
 * with its same-origin requests (and the expo plugin's async storage does not exist there).
 */
export async function getAuthCookieHeader(): Promise<string> {
  if (Platform.OS === "web") {
    return "";
  }
  return getAuthClient().getCookie();
}
